// AGC: SorteioParticipants - Sortable/filterable participant table for admin panel
// AGC: Displays all entries for a given sorteio with search, sort, and CSV export

import React, { useState, useMemo } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { getBackendUrl } from '../../utils/backendUrl'
import { Search, Download, ChevronUp, ChevronDown } from 'lucide-react'
import type { SorteioEntry } from '../../types/sorteio'

// AGC: Parse Firestore timestamps (object with _seconds) or ISO strings safely
const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (typeof val === 'string') { const d = new Date(val); return isNaN(d.getTime()) ? null : d }
  if (typeof val === 'object' && val !== null && '_seconds' in val) return new Date((val as { _seconds: number })._seconds * 1000)
  if (typeof val === 'object' && val !== null && 'seconds' in val) return new Date((val as { seconds: number }).seconds * 1000)
  return null
}
const formatDate = (val: unknown): string => {
  const d = parseDate(val)
  return d ? d.toLocaleDateString() : '—'
}

// AGC: Column sort direction type
type SortDir = 'asc' | 'desc'

// AGC: Sortable column keys
type SortKey = 'userName' | 'userLastName' | 'instagramHandle' | 'luckyNumber' | 'questStatus' | 'completedItems' | 'createdAt'

interface SorteioParticipantsProps {
  entries: SorteioEntry[]
  sorteioId: string
}

// AGC: Status badge color mapping
const STATUS_CLASSES: Record<SorteioEntry['questStatus'], string> = {
  completed: 'bg-green-900/40 text-green-300 border border-green-700',
  expired: 'bg-red-900/40 text-red-300 border border-red-700',
  in_progress: 'bg-yellow-900/40 text-yellow-300 border border-yellow-700',
}

// AGC: Status label keys mapping
const STATUS_LABEL_KEYS: Record<SorteioEntry['questStatus'], string> = {
  completed: 'sorteio.status.questCompleted',
  expired: 'sorteio.status.expired',
  in_progress: 'sorteio.status.inProgress',
}

export const SorteioParticipants: React.FC<SorteioParticipantsProps> = ({ entries, sorteioId }) => {
  const { t } = useTranslation()

  // AGC: Search filter state
  const [search, setSearch] = useState('')

  // AGC: Sort state
  const [sortKey, setSortKey] = useState<SortKey>('createdAt')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // AGC: Handle column header click to toggle sort
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  // AGC: Filter and sort entries based on search and sort state
  const filteredEntries = useMemo(() => {
    const query = search.toLowerCase().trim()
    const filtered = query
      ? entries.filter(e =>
          `${e.userName} ${e.userLastName}`.toLowerCase().includes(query) ||
          (e.instagramHandle ?? '').toLowerCase().includes(query) ||
          (e.luckyNumber ?? '').toLowerCase().includes(query)
        )
      : entries

    return [...filtered].sort((a, b) => {
      let aVal: string | number = ''
      let bVal: string | number = ''

      switch (sortKey) {
        case 'userName':
          aVal = a.userName.toLowerCase()
          bVal = b.userName.toLowerCase()
          break
        case 'userLastName':
          aVal = a.userLastName.toLowerCase()
          bVal = b.userLastName.toLowerCase()
          break
        case 'instagramHandle':
          aVal = (a.instagramHandle ?? '').toLowerCase()
          bVal = (b.instagramHandle ?? '').toLowerCase()
          break
        case 'luckyNumber':
          aVal = a.luckyNumber ?? ''
          bVal = b.luckyNumber ?? ''
          break
        case 'questStatus':
          aVal = a.questStatus
          bVal = b.questStatus
          break
        case 'completedItems':
          aVal = a.completedItems
          bVal = b.completedItems
          break
        case 'createdAt':
          aVal = a.createdAt
          bVal = b.createdAt
          break
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [entries, search, sortKey, sortDir])

  // AGC: Open CSV export in new tab (no auth token needed — browser handles auth cookie or token via URL)
  const handleExportCSV = () => {
    window.open(`${getBackendUrl()}/api/v1/sorteios/${sorteioId}/entries/csv`, '_blank')
  }

  // AGC: Render sort indicator arrow for column header
  const SortIcon: React.FC<{ col: SortKey }> = ({ col }) => {
    if (sortKey !== col) return <ChevronUp className="w-3 h-3 opacity-30" />
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 text-green-400" />
      : <ChevronDown className="w-3 h-3 text-green-400" />
  }

  // AGC: Table header cell with sort support
  const SortableTh: React.FC<{ col: SortKey; label: string; className?: string }> = ({ col, label, className }) => (
    <th
      className={`px-4 py-3 text-left font-medium text-slate-400 dark:text-slate-500 cursor-pointer hover:text-slate-900 dark:hover:text-white select-none ${className ?? ''}`}
      onClick={() => handleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        <SortIcon col={col} />
      </span>
    </th>
  )

  return (
    <div className="space-y-4">
      {/* AGC: Search and export bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('sorteio.admin.search')}
            className="w-full pl-9 pr-4 py-2 bg-slate-100 dark:bg-admin-bg-dark border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          />
        </div>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-white/[0.04] hover:bg-slate-200 dark:hover:bg-white/10 text-slate-700 dark:text-slate-200 rounded-lg transition-colors text-sm border border-slate-200 dark:border-white/10"
        >
          <Download className="w-4 h-4 text-green-400" />
          {t('sorteio.admin.exportCSV')}
        </button>
      </div>

      {/* AGC: Results count */}
      <p className="text-sm text-slate-400 dark:text-slate-500">
        {filteredEntries.length} / {entries.length} {t('sorteio.admin.tabParticipants').toLowerCase()}
      </p>

      {/* AGC: Participant table */}
      {filteredEntries.length === 0 ? (
        <div className="text-center py-12 text-slate-500 dark:text-slate-400">
          {t('sorteio.admin.noEntries')}
        </div>
      ) : (
        <div className="bg-white dark:bg-admin-card-dark rounded-xl border border-slate-200 dark:border-white/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-white/[0.02]">
                <tr>
                  <SortableTh col="userName" label={t('sorteio.admin.colName')} />
                  <SortableTh col="userLastName" label={t('sorteio.admin.colLastName')} />
                  <SortableTh col="instagramHandle" label={t('sorteio.admin.colInstagram')} />
                  <SortableTh col="luckyNumber" label={t('sorteio.admin.colNumber')} />
                  <SortableTh col="questStatus" label={t('sorteio.admin.colStatus')} />
                  <SortableTh col="completedItems" label={t('sorteio.admin.colItems')} />
                  <SortableTh col="createdAt" label={t('sorteio.admin.colEntry')} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filteredEntries.map(entry => (
                  <tr
                    key={`${entry.sorteioId}-${entry.userId}`}
                    className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors"
                  >
                    {/* AGC: First name */}
                    <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{entry.userName}</td>

                    {/* AGC: Last name */}
                    <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{entry.userLastName}</td>

                    {/* AGC: Instagram handle as clickable link */}
                    <td className="px-4 py-3">
                      {entry.instagramHandle ? (
                        <a
                          href={`https://instagram.com/${entry.instagramHandle.replace(/^@/, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 hover:underline"
                        >
                          @{entry.instagramHandle.replace(/^@/, '')}
                        </a>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">—</span>
                      )}
                    </td>

                    {/* AGC: Lucky number in yellow mono font */}
                    <td className="px-4 py-3">
                      {entry.luckyNumber ? (
                        <span className="font-mono font-semibold text-yellow-400">
                          {entry.luckyNumber}
                        </span>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400 font-mono">—</span>
                      )}
                    </td>

                    {/* AGC: Status badge */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLASSES[entry.questStatus]}`}>
                        {t(STATUS_LABEL_KEYS[entry.questStatus])}
                      </span>
                    </td>

                    {/* AGC: Completed items out of 11 */}
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      {entry.completedItems}/11
                    </td>

                    {/* AGC: Entry date */}
                    <td className="px-4 py-3 text-slate-400 dark:text-slate-500 text-xs">
                      {formatDate(entry.questStartedAt || entry.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
